import React, { useState } from "react";
import Xarrow from "react-xarrows";
import styled from "styled-components";
import { DeleteGrayIcon } from "../../../CommonUI/Icon/DeleteGrayIcon";
import { PlusBlueIcon } from "../../../CommonUI/Icon/PlusBlueIcon";
import { EditGrayIcon } from "../../../CommonUI/Icon/EditGrayIcon";

import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { ExpressionView } from "../../../Expressions/ExpressionView";
import { TreeRuleConstant } from "../../../rule-engine-core/Rule/Tree/Common/TreeRuleConstant";
import { Error } from "../../Table/CommonUI/Error";
import { Connector } from "./Connector";

import { TreeRuleNodeProps, XTreeRuleNode } from "./XTreeRuleNode";
import { ObserveMultiState } from "../../../CommonUI/StateManagement/ObserveMultiState";
import { TestContext } from "../../../CommonUI/Testing/TestContext";
import {
    ExpressionReactContext,
    ExpressionRenderType,
    expressionSettings,
} from "../../../Expressions/Structure/ExpressionReactContext";
import { TestingConstants } from "../../../CommonUI/Testing/TestingConstants";
import { ExpressionTestResultColorContext } from "../../../Expressions/Testing/ExpressionTestResultColorContext";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { ConfirmDialog } from "../../../CommonUI/ConfirmDialog";
import { TreeRuleNodeType } from "../../../rule-engine-core/Rule/Tree/Structure/TreeRuleNode";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";

export const EvaluationNode = ({ node, editContext, treeRuleBuilder, ruleDefinition }: TreeRuleNodeProps) => {
    const testContext = React.useContext(TestContext);

    const [mounted, setMounted] = React.useState<boolean>(false);

    const [openEditLayer, setOpenEditLayer] = React.useState<boolean>(false);

    const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);

    const onDelete = () => {
        if (
            (node.expression.isEmpty &&
                node.trueNode!.nodeType === TreeRuleNodeType.UNDEFINED &&
                node.falseNode!.nodeType === TreeRuleNodeType.UNDEFINED) ||
            DataUtilities.getCookie(TreeRuleConstant.TREE_RULE_CLEAR_COOKIE_KEY) === "true"
        ) {
            node.delete();
            StateManager.changed(node);
            StateManager.changed(ruleDefinition);
        } else {
            setOpenConfirmDialog(true);
        }
    };

    React.useLayoutEffect(() => {
        setTimeout(() => {
            setMounted(true);
        }, 100);
    }, []);

    const onInsert = (isTrue: boolean) => {
        ruleDefinition.insert(node, isTrue);
        StateManager.changed(node);
        StateManager.changed(ruleDefinition);
    };

    const onEdit = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setOpenEditLayer(true);
    };

    if (testContext) {
        const currentTestItem = testContext.findItem(node.expression._id);

        let testcolor: string | undefined;

        if (currentTestItem) {
            if (currentTestItem.result === "true") {
                testcolor = TestingConstants.TEXT_SUCCESS;
            } else {
                testcolor = TestingConstants.TEXT_FAIL;
            }
        }

        return (
            <EvaluationNodeRender>
                <HorizontalRender>
                    <ExpressionRender
                        id={node._id}
                        color={node.color}
                        isTestPassed={!!currentTestItem}
                        isError={currentTestItem?.isError}
                    >
                        <div className="TreeRuleNode-expression">
                            <ExpressionTestResultColorContext.Provider value={testcolor}>
                                <ExpressionReactContext.Provider value={expressionSettings[ExpressionRenderType.TEXT]}>
                                    <ExpressionView
                                        expression={node.expression}
                                        color={{
                                            expression: UIStyleConstants.BLUE_COLOR,
                                            result: UIStyleConstants.BLUE_COLOR,
                                        }}
                                        hideValidation
                                    />
                                </ExpressionReactContext.Provider>
                            </ExpressionTestResultColorContext.Provider>
                        </div>
                        <Connector nodeid={node._id} />
                    </ExpressionRender>

                    <XTreeRuleNode
                        node={node.trueNode!}
                        editContext={editContext}
                        treeRuleBuilder={treeRuleBuilder}
                        ruleDefinition={ruleDefinition}
                    />

                    {mounted && !!node.trueNode && (
                        <RightLine
                            start={node._id}
                            end={node.trueNode!._id}
                            onInsert={() => {}}
                            isTestPassed={!!currentTestItem && currentTestItem.result === "true"}
                        />
                    )}
                </HorizontalRender>
                <VerticalRender>
                    <XTreeRuleNode
                        node={node.falseNode!}
                        editContext={editContext}
                        ruleDefinition={ruleDefinition}
                    />

                    {!!node.falseNode && (
                        <BottomLine
                            start={node._id}
                            end={node.falseNode!._id}
                            onInsert={() => {}}
                            isTestPassed={!!currentTestItem && currentTestItem.result === "false"}
                        />
                    )}
                </VerticalRender>
            </EvaluationNodeRender>
        );
    }

    return (
        <EvaluationNodeRender>
            <HorizontalRender>
                <ExpressionRender
                    id={node._id}
                    color={node.color}
                >
                    <div className="TreeRuleNode-expression">
                        <ExpressionView
                            expression={node.expression}
                            onDone={() => {
                                setOpenEditLayer(false);
                                StateManager.propertyChanged(node, "expression");
                                StateManager.changed(ruleDefinition);
                            }}
                            color={{
                                expression: UIStyleConstants.BLUE_COLOR,
                                result: UIStyleConstants.BLUE_COLOR,
                            }}
                            hideValidation
                            open={openEditLayer}
                        />
                    </div>
                    {<Error issues={node.getDesignValidationIssues()} />}
                    <div className="Actions-group">
                        <StyledButton onClick={onEdit}>
                            <EditGrayIcon />
                        </StyledButton>
                        {ruleDefinition.root !== node && (
                            <StyledButton onClick={onDelete}>
                                <DeleteGrayIcon />
                            </StyledButton>
                        )}
                    </div>
                    <Connector nodeid={node._id} />
                </ExpressionRender>

                <XTreeRuleNode
                    node={node.trueNode!}
                    editContext={editContext}
                    ruleDefinition={ruleDefinition}
                />

                <ObserveMultiState
                    listenTo={[ruleDefinition, node.trueNode!]}
                    control={() => {
                        if (!node.trueNode) return null;
                        return (
                            <RightLine
                                start={node._id}
                                end={node.trueNode!._id}
                                onInsert={() => onInsert(true)}
                            />
                        );
                    }}
                />
            </HorizontalRender>
            <VerticalRender>
                <XTreeRuleNode
                    node={node.falseNode!}
                    editContext={editContext}
                    ruleDefinition={ruleDefinition}
                />

                <ObserveMultiState
                    listenTo={[ruleDefinition, node.falseNode!]}
                    control={() => {
                        if (!node.falseNode) return null;
                        return (
                            <BottomLine
                                start={node._id}
                                end={node.falseNode!._id}
                                onInsert={() => onInsert(false)}
                            />
                        );
                    }}
                />
            </VerticalRender>

            <ConfirmDialog
                open={openConfirmDialog}
                onConfirm={(dontAskAgain) => {
                    if (dontAskAgain) {
                        DataUtilities.setCookieValue(TreeRuleConstant.TREE_RULE_CLEAR_COOKIE_KEY, "true");
                    }

                    node.delete();
                    StateManager.changed(node);
                    StateManager.changed(ruleDefinition);
                    setOpenConfirmDialog(false);
                }}
                onCancel={() => setOpenConfirmDialog(false)}
            />
        </EvaluationNodeRender>
    );
};

interface LineProps {
    start: string;
    end: string;
    onInsert: () => void;
    isTestPassed?: boolean;
}

const RightLine = ({ start, end, onInsert, isTestPassed }: LineProps) => {
    const testContext = React.useContext(TestContext);

    if (testContext) {
        return (
            <Xarrow
                start={`${start}-right`}
                end={`${end}-left`}
                path="straight"
                color={isTestPassed ? TestingConstants.BORDER_PASSED : TreeRuleConstant.LINE_COLOR}
                headSize={5}
                labels={{
                    start: <RightLineLabel>True</RightLineLabel>,
                }}
                strokeWidth={isTestPassed ? 3 : 1}
                startAnchor="right"
                endAnchor="left"
                data-testid={`${start}-${end}`}
            />
        );
    }

    return (
        <Xarrow
            start={`${start}-right`}
            end={`${end}-left`}
            path="straight"
            color={TreeRuleConstant.LINE_COLOR}
            headSize={5}
            labels={{
                start: (
                    <RightLineLabel>
                        True
                        <InsertButton
                            onClick={() => onInsert()}
                            className={"BtnTreeRuleNode-insert"}
                            data-testid={`${start}-rightline-insert`}
                        >
                            <PlusBlueIcon />
                        </InsertButton>
                    </RightLineLabel>
                ),
            }}
            strokeWidth={1}
            startAnchor="right"
            endAnchor="left"
            data-testid={`${start}-${end}`}
        />
    );
};

const BottomLine = ({ start, end, onInsert, isTestPassed }: LineProps) => {
    const testContext = React.useContext(TestContext);

    if (testContext) {
        return (
            <Xarrow
                start={`${start}-bottom`}
                end={`${end}-top`}
                path="straight"
                color={isTestPassed ? TestingConstants.BORDER_PASSED : TreeRuleConstant.LINE_COLOR}
                headSize={5}
                labels={{
                    middle: <BottomLineLabel>False</BottomLineLabel>,
                }}
                strokeWidth={isTestPassed ? 3 : 1}
                startAnchor={"bottom"}
                endAnchor={"top"}
            />
        );
    }

    return (
        <Xarrow
            start={`${start}-bottom`}
            end={`${end}-top`}
            path="straight"
            color={TreeRuleConstant.LINE_COLOR}
            headSize={5}
            labels={{
                middle: (
                    <BottomLineLabel>
                        False
                        <InsertButton
                            onClick={() => onInsert()}
                            className={"BtnTreeRuleNode-insert"}
                            data-testid={`${start}-bottomline-insert`}
                        >
                            <PlusBlueIcon />
                        </InsertButton>
                    </BottomLineLabel>
                ),
            }}
            strokeWidth={1}
            startAnchor={"bottom"}
            endAnchor={"top"}
        />
    );
};

const EvaluationNodeRender = styled.div`
    background: transparent;
    box-sizing: border-box;
    border-radius: 7px;

    display: flex;
    flex-direction: column;
`;

const ExpressionRender = styled.div<{ color: string; isTestPassed?: boolean; isError?: boolean }>`
    ${(props) =>
        props.isError
            ? `
                border: 2px solid ${TestingConstants.BORDER_FAIL};
                background: ${TestingConstants.BACKGROUND_FAIL};
            `
            : props.isTestPassed
              ? `border: 2px solid ${TestingConstants.BORDER_PASSED}`
              : `border: 2px solid transparent;`};

    box-sizing: border-box;
    border-radius: 4px;

    padding: 4px;

    margin-right: ${TreeRuleConstant.DIFF_TRUE_X}px;

    position: relative;

    &:hover {
        border: 2px solid ${TreeRuleConstant.SELECTED_NODE_BORDER_COLOR};

        .Actions-group {
            opacity: 1;
        }
    }

    .TreeRuleNode-expression {
        padding: 0 4px;
        border: 1px solid ${UIStyleConstants.GRAY_LIGHTER_COLOR};
        border-radius: 4px;

        background: ${(props) => (props.isError ? TestingConstants.BACKGROUND_FAIL : props.color)};

        .ExpressionView-root {
            background: ${(props) => (props.isError ? TestingConstants.BACKGROUND_FAIL : UIStyleConstants.WHITE_COLOR)};
        }
    }

    .Actions-group {
        opacity: 0;

        transition: opacity 0.1s ease-in;

        background: ${UIStyleConstants.WHITE_COLOR};
        border: 1px solid ${UIStyleConstants.GRAY_VERY_LIGHT_COLOR};
        box-sizing: border-box;
        border-radius: 4px;

        display: flex;
        align-items: center;

        position: absolute;

        right: 0;
        top: -30px;
    }
`;

const RightLineLabel = styled.div`
  display: flex;
  flex-direction: row;
  color ${UIStyleConstants.GREEN_COLOR};;

  .BtnTreeRuleNode-insert {
    margin-left: 20px;
  }

  .BtnTreeRuleNode-insert {
    opacity: 0;
  }

  &:hover .BtnTreeRuleNode-insert {
    opacity: 1;
  }

  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
  letter-spacing: 0px;
  text-align: left;
`;

const BottomLineLabel = styled.div`
    display: flex;
    flex-direction: column;
    color: ${UIStyleConstants.RED_COLOR};

    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 17px;
    letter-spacing: 0px;
    text-align: left;
    margin-left: 50px;

    .BtnTreeRuleNode-insert {
        margin-top: 20px;
    }

    .BtnTreeRuleNode-insert {
        opacity: 0;
    }

    &:hover .BtnTreeRuleNode-insert {
        opacity: 1;
    }
`;

const InsertButton = styled.div`
    cursor: pointer;
`;

const HorizontalRender = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
`;

const VerticalRender = styled.div`
    display: flex;
    margin-top: ${TreeRuleConstant.DIFF_FALSE_Y}px;
`;

export const StyledButton = styled.div`
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    cursor: pointer;
`;
